/**
 * How a goal's numbers read on the item page: "now $11.4M · target $13.2M",
 * "now 61 · target 84 controls", "now 99.41% · target 99.5%".
 *
 * Goals store current and target as text with a separate unit (goals.unit).
 * A number with a currency code for its unit reads as money, large numbers are
 * shortened (13200000 USD → $13.2M), and anything that is not a number is shown
 * as written.
 */
import { formatNumber } from '@/i18n/formatters';

const CURRENCY = /^[A-Z]{3}$/;

export function formatGoalValue(raw: string, unit: string | null | undefined): string {
  const u = unit?.trim() ?? '';
  const n = Number(raw.replace(/,/g, '').trim());
  if (raw.trim() === '' || !Number.isFinite(n)) return u && u !== '%' ? `${raw} ${u}` : `${raw}${u}`;
  const big = Math.abs(n) >= 10_000;
  if (CURRENCY.test(u) || u === '$') {
    try {
      return formatNumber(n, {
        style: 'currency',
        currency: u === '$' ? 'USD' : u,
        notation: big ? 'compact' : 'standard',
        minimumFractionDigits: big || Number.isInteger(n) ? 0 : 2,
        maximumFractionDigits: big ? 1 : 2,
      });
    } catch {
      // An unknown code: fall through and show it as a unit.
    }
  }
  const num = formatNumber(n, { notation: Math.abs(n) >= 100_000 ? 'compact' : 'standard', maximumFractionDigits: 2 });
  if (!u) return num;
  return u === '%' ? `${num}%` : `${num} ${u}`;
}

/** The line under a goal: its metric (when it says more than the name), then now · target. */
export function goalMeasure(g: {
  name: string;
  metricName: string | null;
  currentValue: string | null;
  targetValue: string | null;
  unit: string | null;
}): string | null {
  const measure = [
    g.currentValue ? `now ${formatGoalValue(g.currentValue, g.unit)}` : null,
    g.targetValue ? `target ${formatGoalValue(g.targetValue, g.unit)}` : null,
  ]
    .filter(Boolean)
    .join(' · ');
  return [g.metricName && g.metricName !== g.name ? g.metricName : null, measure || null].filter(Boolean).join(': ') || null;
}
